/* eslint-disable @typescript-eslint/member-ordering */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { IonInput, IonItem, IonSelect, IonSelectOption } from '@ionic/angular/standalone';
import { MarkdownModule } from 'ngx-markdown';
import type {
  SuperLatexConnection,
  SuperLatexConnectionStyle,
  SuperLatexContent,
  TailwindColor,
} from '../super-latex.model';

type EditorMode = 'view' | 'create-anchor' | 'select-anchor' | 'connect-from' | 'connect-to';

interface HoveredElement {
  element: HTMLElement;
  text: string;
  rect: DOMRect;
  nodeIndex: number;
  isExistingAnchor: boolean;
  anchorId?: string;
}

interface LassoState {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  isDragging: boolean;
}

interface LassoSelection {
  elements: HTMLElement[];
  texts: string[];
  nodeIndex: number;
  combinedRect: DOMRect;
}

@Component({
  selector: 'dojo-super-latex-editor',
  standalone: true,
  imports: [FormsModule, MarkdownModule, ButtonComponent, IconComponent, IonInput, IonItem, IonSelect, IonSelectOption],
  templateUrl: './super-latex-editor.component.html',
  styleUrl: './super-latex-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperLatexEditorComponent {
  constructor() {
    effect(() => {
      this.content();
      // Reset state when content changes to avoid stale references
      setTimeout(() => {
        this.hoveredElement.set(null);
        this.hoverableElements.set([]);
        this.hoverLevel.set(0);
        this.lasso.set(null);
        this.lassoSelection.set(null);
        this.clickTargetElement = null;
      }, 50);
    });
  }

  readonly content = input.required<SuperLatexContent>();
  readonly contentChange = output<SuperLatexContent>();
  readonly editorClose = output<void>();

  readonly latexContainer = viewChild<ElementRef>('latexContainer');

  // Editor state
  readonly mode = signal<EditorMode>('view');
  readonly hoveredElement = signal<HoveredElement | null>(null);
  readonly hoverableElements = signal<HoveredElement[]>([]); // All possible selection levels
  readonly hoverLevel = signal(0); // Current selection level (0 = smallest)
  readonly selectedAnchors = signal<string[]>([]);
  readonly connectFromAnchor = signal<string | null>(null);
  readonly selectedConnection = signal<SuperLatexConnection | null>(null);

  // Lasso selection state
  readonly lasso = signal<LassoState | null>(null);
  readonly lassoSelection = signal<LassoSelection | null>(null);
  private clickTargetElement: HoveredElement | null = null; // Save hover before lasso clears it

  // Anchor creation state
  readonly showAnchorDialog = signal(false);
  readonly pendingAnchor = signal<{ nodeIndex: number; text: string } | null>(null);
  readonly newAnchorId = signal('');

  // Connection editor state
  readonly editingConnection = signal<SuperLatexConnection | null>(null);

  readonly katexOptions = {
    throwOnError: false,
    strict: 'warn' as const,
    trust: true,
  };

  readonly existingAnchors = computed(() => {
    const content = this.content();
    const anchors: Array<{ id: string; nodeIndex: number }> = [];

    content.latex_nodes.forEach((node, index) => {
      const matches = node.latex.match(/anchor=([a-zA-Z0-9_-]+)/g);
      if (matches) {
        for (const match of matches) {
          anchors.push({
            id: match.replace('anchor=', ''),
            nodeIndex: index,
          });
        }
      }
    });

    return anchors;
  });

  readonly connections = computed(() => this.content().connections ?? []);

  readonly colorOptions: TailwindColor[] = [
    'neutral',
    'red',
    'orange',
    'amber',
    'green',
    'teal',
    'sky',
    'indigo',
    'purple',
    'pink',
  ];

  onLatexMouseMove(event: MouseEvent): void {
    const currentMode = this.mode();
    if (currentMode === 'view') {
      this.hoveredElement.set(null);
      this.hoverableElements.set([]);
      return;
    }

    const container = this.latexContainer()?.nativeElement;
    if (!container) return;

    const target = event.target as HTMLElement;

    // First check if it's an existing anchor
    const anchorEl = target.closest('[data-anchor]') as HTMLElement | null;
    if (anchorEl) {
      const anchorId = anchorEl.getAttribute('data-anchor');
      const nodeIndex = this.findNodeIndexForElement(anchorEl, container);
      if (anchorId && nodeIndex !== -1) {
        const anchorHovered: HoveredElement = {
          element: anchorEl,
          text: anchorEl.textContent || '',
          rect: anchorEl.getBoundingClientRect(),
          nodeIndex,
          isExistingAnchor: true,
          anchorId,
        };
        this.hoveredElement.set(anchorHovered);
        this.hoverableElements.set([anchorHovered]);
        return;
      }
    }

    // For create-anchor mode, find all hoverable KaTeX elements at different levels
    if (currentMode === 'create-anchor') {
      const allLevels = this.findAllHoverableLevels(target, container);
      if (allLevels.length > 0) {
        this.hoverableElements.set(allLevels);
        // Clamp hover level to available levels
        const level = Math.min(this.hoverLevel(), allLevels.length - 1);
        this.hoveredElement.set(allLevels[level]);
        return;
      }
    }

    this.hoveredElement.set(null);
    this.hoverableElements.set([]);
  }

  onLatexWheel(event: WheelEvent): void {
    const currentMode = this.mode();
    if (currentMode !== 'create-anchor') return;

    // Don't interfere with lasso
    if (this.lasso()?.isDragging) return;

    const levels = this.hoverableElements();
    if (levels.length <= 1) return;

    event.preventDefault();

    const currentLevel = this.hoverLevel();
    let newLevel: number;

    if (event.deltaY > 0) {
      // Scroll down = select larger (parent) element
      newLevel = Math.min(currentLevel + 1, levels.length - 1);
    } else {
      // Scroll up = select smaller (child) element
      newLevel = Math.max(currentLevel - 1, 0);
    }

    if (newLevel !== currentLevel) {
      this.hoverLevel.set(newLevel);
      this.hoveredElement.set(levels[newLevel]);
    }
  }

  onLatexMouseDown(event: MouseEvent): void {
    const currentMode = this.mode();

    // Only use lasso for create-anchor mode
    if (currentMode !== 'create-anchor') return;

    const container = this.latexContainer()?.nativeElement;
    if (!container) return;

    // Don't start lasso if clicking on existing anchor
    const target = event.target as HTMLElement;
    if (target.closest('[data-anchor]')) return;

    const containerRect = container.getBoundingClientRect();
    const x = event.clientX - containerRect.left + container.scrollLeft;
    const y = event.clientY - containerRect.top + container.scrollTop;

    // Save the current hover target for potential click
    this.clickTargetElement = this.hoveredElement();

    this.lasso.set({
      startX: x,
      startY: y,
      currentX: x,
      currentY: y,
      isDragging: true,
    });

    this.lassoSelection.set(null);
  }

  onLatexMouseMoveLasso(event: MouseEvent): void {
    const lassoState = this.lasso();
    if (!lassoState?.isDragging) return;

    const container = this.latexContainer()?.nativeElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const x = event.clientX - containerRect.left + container.scrollLeft;
    const y = event.clientY - containerRect.top + container.scrollTop;

    this.lasso.set({
      ...lassoState,
      currentX: x,
      currentY: y,
    });

    // Find elements within lasso
    this.updateLassoSelection();
  }

  onLatexMouseUp(_event: MouseEvent): void {
    const lassoState = this.lasso();
    if (!lassoState?.isDragging) {
      this.clickTargetElement = null;
      return;
    }

    const selection = this.lassoSelection();

    // Check if it was a significant drag (not just a click)
    const dragDistance = Math.sqrt(
      Math.pow(lassoState.currentX - lassoState.startX, 2) + Math.pow(lassoState.currentY - lassoState.startY, 2),
    );

    if (dragDistance > 10 && selection && selection.texts.length > 0) {
      // It was a real drag - handle lasso selection
      const combinedText = selection.texts.join('');
      this.pendingAnchor.set({
        nodeIndex: selection.nodeIndex,
        text: combinedText,
      });
      this.newAnchorId.set(this.suggestAnchorId(combinedText));
      this.showAnchorDialog.set(true);
    }
    // Small drags (clicks) are handled by onLatexClick

    this.lasso.set(null);
    this.lassoSelection.set(null);
  }

  private updateLassoSelection(): void {
    const lassoState = this.lasso();
    if (!lassoState) return;

    const container = this.latexContainer()?.nativeElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();

    // Calculate lasso rectangle in viewport coordinates
    const lassoLeft = Math.min(lassoState.startX, lassoState.currentX);
    const lassoTop = Math.min(lassoState.startY, lassoState.currentY);
    const lassoRight = Math.max(lassoState.startX, lassoState.currentX);
    const lassoBottom = Math.max(lassoState.startY, lassoState.currentY);

    // Convert to viewport coordinates for intersection testing
    const lassoViewportLeft = lassoLeft + containerRect.left - container.scrollLeft;
    const lassoViewportTop = lassoTop + containerRect.top - container.scrollTop;
    const lassoViewportRight = lassoRight + containerRect.left - container.scrollLeft;
    const lassoViewportBottom = lassoBottom + containerRect.top - container.scrollTop;

    // Find all leaf KaTeX elements
    const katexElements = container.querySelectorAll('.mord, .mbin, .mrel, .mop, .mopen, .mclose, .mpunct');
    const selectedElements: HTMLElement[] = [];
    const selectedTexts: string[] = [];
    let nodeIndex = -1;

    for (const el of katexElements) {
      const htmlEl = el as HTMLElement;

      // Skip if inside an existing anchor
      if (htmlEl.closest('[data-anchor]')) continue;

      // Skip if it has children that are also katex elements (we want leaf nodes)
      if (htmlEl.querySelector('.mord, .mbin, .mrel, .mop')) continue;

      const rect = htmlEl.getBoundingClientRect();

      // Check if element intersects with lasso
      if (
        rect.left < lassoViewportRight &&
        rect.right > lassoViewportLeft &&
        rect.top < lassoViewportBottom &&
        rect.bottom > lassoViewportTop
      ) {
        const text = htmlEl.textContent?.trim() || '';
        if (text) {
          selectedElements.push(htmlEl);
          selectedTexts.push(text);

          if (nodeIndex === -1) {
            nodeIndex = this.findNodeIndexForElement(htmlEl, container);
          }
        }
      }
    }

    if (selectedElements.length > 0) {
      // Calculate combined bounding rect
      const rects = selectedElements.map((el) => el.getBoundingClientRect());
      const combinedRect = new DOMRect(
        Math.min(...rects.map((r) => r.left)),
        Math.min(...rects.map((r) => r.top)),
        Math.max(...rects.map((r) => r.right)) - Math.min(...rects.map((r) => r.left)),
        Math.max(...rects.map((r) => r.bottom)) - Math.min(...rects.map((r) => r.top)),
      );

      this.lassoSelection.set({
        elements: selectedElements,
        texts: selectedTexts,
        nodeIndex,
        combinedRect,
      });
    } else {
      this.lassoSelection.set(null);
    }
  }

  getLassoRect(): { left: number; top: number; width: number; height: number } | null {
    const lassoState = this.lasso();
    if (!lassoState?.isDragging) return null;

    return {
      left: Math.min(lassoState.startX, lassoState.currentX),
      top: Math.min(lassoState.startY, lassoState.currentY),
      width: Math.abs(lassoState.currentX - lassoState.startX),
      height: Math.abs(lassoState.currentY - lassoState.startY),
    };
  }

  private findAllHoverableLevels(target: HTMLElement, container: HTMLElement): HoveredElement[] {
    const levels: HoveredElement[] = [];
    let current: HTMLElement | null = target;

    while (current && current !== container) {
      // Skip if it's already an anchor
      if (current.hasAttribute('data-anchor')) {
        return levels;
      }

      // Look for KaTeX elements with meaningful content
      const isKatexElement =
        current.classList.contains('mord') ||
        current.classList.contains('mbin') ||
        current.classList.contains('mrel') ||
        current.classList.contains('mopen') ||
        current.classList.contains('mclose') ||
        current.classList.contains('mpunct') ||
        current.classList.contains('minner') ||
        current.classList.contains('mop') ||
        current.classList.contains('msupsub') ||
        current.classList.contains('base') ||
        (current.classList.contains('strut') === false && current.classList.contains('katex-html'));

      if (isKatexElement) {
        const text = current.textContent?.trim() || '';
        if (text && text.length > 0 && text.length < 100) {
          const nodeIndex = this.findNodeIndexForElement(current, container);
          if (nodeIndex !== -1) {
            // Avoid duplicates (same text content)
            const alreadyExists = levels.some((l) => l.text === text);
            if (!alreadyExists) {
              levels.push({
                element: current,
                text,
                rect: current.getBoundingClientRect(),
                nodeIndex,
                isExistingAnchor: false,
              });
            }
          }
        }
      }

      current = current.parentElement;
    }

    return levels; // Smallest to largest
  }

  private findHoverableElement(target: HTMLElement, container: HTMLElement): HoveredElement | null {
    const levels = this.findAllHoverableLevels(target, container);
    return levels.length > 0 ? levels[0] : null;
  }

  private findNodeIndexForElement(element: HTMLElement, container: HTMLElement): number {
    const latexLines = container.querySelectorAll('.latex-line');
    for (let i = 0; i < latexLines.length; i++) {
      if (latexLines[i].contains(element)) {
        return i;
      }
    }
    return -1;
  }

  onLatexClick(event: MouseEvent): void {
    const currentMode = this.mode();

    // For create-anchor mode, handle clicking on non-anchor elements
    if (currentMode === 'create-anchor') {
      // If lasso was active, it's handled in mouseUp
      if (this.lasso()) return;

      // Otherwise handle as a simple click on hovered element
      const hovered = this.hoveredElement() || this.clickTargetElement;
      if (hovered && !hovered.isExistingAnchor) {
        this.pendingAnchor.set({
          nodeIndex: hovered.nodeIndex,
          text: hovered.text,
        });
        this.newAnchorId.set(this.suggestAnchorId(hovered.text));
        this.showAnchorDialog.set(true);
      }
      this.clickTargetElement = null;
      return;
    }

    // For other modes, get hovered element directly from event target
    const target = event.target as HTMLElement;
    const container = this.latexContainer()?.nativeElement;
    if (!container) return;

    const anchorEl = target.closest('[data-anchor]') as HTMLElement | null;
    if (!anchorEl) return;

    const anchorId = anchorEl.getAttribute('data-anchor');
    if (!anchorId) return;

    if (currentMode === 'select-anchor') {
      const current = this.selectedAnchors();
      if (current.includes(anchorId)) {
        this.selectedAnchors.set(current.filter((id) => id !== anchorId));
      } else {
        this.selectedAnchors.set([...current, anchorId]);
      }
    } else if (currentMode === 'connect-from') {
      this.connectFromAnchor.set(anchorId);
      this.mode.set('connect-to');
    } else if (currentMode === 'connect-to') {
      const fromAnchor = this.connectFromAnchor();
      if (fromAnchor && fromAnchor !== anchorId) {
        this.createConnection(fromAnchor, anchorId);
      }
      this.connectFromAnchor.set(null);
      this.mode.set('view');
    }
  }

  private suggestAnchorId(text: string): string {
    // Generate a suggested anchor ID from the text
    const cleaned = text
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 20);
    return cleaned || 'anchor';
  }

  confirmCreateAnchor(): void {
    const pending = this.pendingAnchor();
    const anchorId = this.newAnchorId().trim();

    if (!pending || !anchorId) return;

    // Check if anchor ID already exists
    if (this.existingAnchors().some((a) => a.id === anchorId)) {
      alert('Anchor ID already exists. Please choose a different name.');
      return;
    }

    this.createAnchorInLatex(pending.nodeIndex, pending.text, anchorId);
    this.closeAnchorDialog();
  }

  closeAnchorDialog(): void {
    this.showAnchorDialog.set(false);
    this.pendingAnchor.set(null);
    this.newAnchorId.set('');
  }

  private createAnchorInLatex(nodeIndex: number, textContent: string, anchorId: string): void {
    const content = this.content();
    const node = content.latex_nodes[nodeIndex];
    if (!node) {
      alert('Could not find the LaTeX node.');
      return;
    }

    const latex = node.latex;
    let newLatex = latex;
    let replaced = false;
    // Generate possible LaTeX patterns from the rendered text
    const patterns = this.generateLatexPatterns(textContent);

    // Try each pattern until one works
    for (const pattern of patterns) {
      // Skip patterns that are too short or could cause issues
      if (pattern.length < 1) continue;

      // Check if pattern exists and is not inside existing htmlData
      if (latex.includes(pattern) && !this.isInsideHtmlData(latex, pattern)) {
        // Verify the replacement won't create nested htmlData
        const replacement = `\\htmlData{anchor=${anchorId}}{${pattern}}`;
        const testLatex = latex.replace(pattern, replacement);

        // Basic validation - check for obvious issues
        if (
          !testLatex.includes('\\htmlData{anchor=') ||
          this.countOccurrences(testLatex, '\\htmlData{') === this.countOccurrences(latex, '\\htmlData{') + 1
        ) {
          newLatex = testLatex;
          replaced = true;
          break;
        }
      }
    }

    if (replaced && newLatex !== latex) {
      // Final validation - make sure braces are balanced
      if (this.areBracesBalanced(newLatex)) {
        const updatedNodes = [...content.latex_nodes];
        updatedNodes[nodeIndex] = { ...node, latex: newLatex };

        const updatedContent: SuperLatexContent = {
          ...content,
          latex_nodes: updatedNodes,
        };

        this.contentChange.emit(updatedContent);
      } else {
        alert(
          `Anchor creation would create invalid LaTeX.\n\n` +
            `Please try selecting a different element or use the hover+scroll method for single elements.`,
        );
      }
    } else {
      // Show error with helpful info
      alert(
        `Could not find a safe match for "${textContent}" in the LaTeX source.\n\n` +
          `This often happens with complex LaTeX (colors, nested structures).\n\n` +
          `Try selecting individual elements using hover+scroll instead of lasso.`,
      );
    }
  }

  private countOccurrences(str: string, substr: string): number {
    let count = 0;
    let pos = 0;
    while ((pos = str.indexOf(substr, pos)) !== -1) {
      count++;
      pos += substr.length;
    }
    return count;
  }

  private areBracesBalanced(str: string): boolean {
    let depth = 0;
    for (const char of str) {
      if (char === '{') depth++;
      else if (char === '}') depth--;
      if (depth < 0) return false;
    }
    return depth === 0;
  }

  private generateLatexPatterns(text: string): string[] {
    const patterns: string[] = [];

    // 1. Exact text as-is
    patterns.push(text);

    // 2. Common function patterns (log, sin, cos, etc.)
    const funcMatch = text.match(/^(log|ln|sin|cos|tan|cot|sec|csc|exp|lim|sum|prod|int)(.*)$/i);
    if (funcMatch) {
      const [, func, rest] = funcMatch;
      const restParts = rest.split('');

      // \func_subscript argument patterns
      if (restParts.length >= 2) {
        const subscript = restParts[0];
        const arg = restParts.slice(1).join('');
        patterns.push(`\\${func}_${subscript} ${arg}`);
        patterns.push(`\\${func}_{${subscript}} ${arg}`);
        patterns.push(`\\${func}_${subscript}${arg}`);
        patterns.push(`\\${func}_{${subscript}}${arg}`);
        patterns.push(`${func}_${subscript} ${arg}`);
        patterns.push(`${func}_{${subscript}} ${arg}`);
      }
      if (restParts.length >= 1) {
        patterns.push(`\\${func} ${rest}`);
        patterns.push(`\\${func}${rest}`);
      }
      patterns.push(`\\${func}`);
    }

    // 3. Text with spaces between characters (for cases like "a b c" -> "abc")
    if (text.length > 1) {
      patterns.push(text.split('').join(' '));
    }

    // 4. With underscores for subscripts (e.g., "x2" -> "x_2")
    const subscriptMatch = text.match(/^([a-zA-Z]+)(\d+)$/);
    if (subscriptMatch) {
      const [, base, sub] = subscriptMatch;
      patterns.push(`${base}_${sub}`);
      patterns.push(`${base}_{${sub}}`);
    }

    // 5. With carets for superscripts
    const superMatch = text.match(/^([a-zA-Z]+)\^?(\d+)$/);
    if (superMatch) {
      const [, base, sup] = superMatch;
      patterns.push(`${base}^${sup}`);
      patterns.push(`${base}^{${sup}}`);
    }

    // 6. Fractions (e.g., "ab" from \frac{a}{b})
    if (text.length === 2) {
      patterns.push(`\\frac{${text[0]}}{${text[1]}}`);
    }

    return patterns;
  }

  private isInsideHtmlData(latex: string, text: string): boolean {
    // Find all htmlData blocks (handling nested braces)
    const findHtmlDataBlocks = (str: string): string[] => {
      const blocks: string[] = [];
      let i = 0;
      while (i < str.length) {
        const start = str.indexOf('\\htmlData{', i);
        if (start === -1) break;

        // Find the matching closing braces for both arguments
        let depth = 0;
        let j = start + 10; // After '\htmlData{'
        let firstArgEnd = -1;

        // Find end of first arg
        while (j < str.length) {
          if (str[j] === '{') depth++;
          else if (str[j] === '}') {
            if (depth === 0) {
              firstArgEnd = j;
              break;
            }
            depth--;
          }
          j++;
        }

        if (firstArgEnd === -1 || str[firstArgEnd + 1] !== '{') {
          i = start + 1;
          continue;
        }

        // Find end of second arg
        j = firstArgEnd + 2;
        depth = 0;
        let secondArgEnd = -1;
        while (j < str.length) {
          if (str[j] === '{') depth++;
          else if (str[j] === '}') {
            if (depth === 0) {
              secondArgEnd = j;
              break;
            }
            depth--;
          }
          j++;
        }

        if (secondArgEnd !== -1) {
          blocks.push(str.slice(start, secondArgEnd + 1));
        }
        i = secondArgEnd !== -1 ? secondArgEnd + 1 : start + 1;
      }
      return blocks;
    };

    const blocks = findHtmlDataBlocks(latex);
    for (const block of blocks) {
      // Check if text appears in the content part (second argument)
      const contentStart = block.indexOf('}{') + 2;
      const content = block.slice(contentStart, -1);
      if (content.includes(text)) {
        return true;
      }
    }
    return false;
  }

  startCreateAnchor(): void {
    this.mode.set('create-anchor');
    this.hoverLevel.set(0);
    this.hoverableElements.set([]);
  }

  startSelectAnchors(): void {
    this.mode.set('select-anchor');
    this.selectedAnchors.set([]);
  }

  startCreateConnection(): void {
    this.mode.set('connect-from');
    this.connectFromAnchor.set(null);
  }

  cancelMode(): void {
    this.mode.set('view');
    this.hoveredElement.set(null);
    this.connectFromAnchor.set(null);
  }

  createConnection(fromAnchor: string, toAnchor: string): void {
    const newConnection: SuperLatexConnection = {
      id: `conn-${fromAnchor}-${toAnchor}`,
      from_anchor: fromAnchor,
      to_anchor: toAnchor,
      style: {
        color: 'neutral',
        width: 1.2,
        line_style: 'solid',
        arrow_style: 'open',
        from_socket: 'Bottom',
        to_socket: 'Top',
        stub: 12,
      },
    };

    const content = this.content();
    const updatedContent: SuperLatexContent = {
      ...content,
      connections: [...(content.connections ?? []), newConnection],
    };

    this.contentChange.emit(updatedContent);
    this.editConnection(newConnection);
  }

  editConnection(connection: SuperLatexConnection): void {
    this.editingConnection.set({ ...connection });
    this.selectedConnection.set(connection);
  }

  deleteConnection(connectionId: string): void {
    const content = this.content();
    const updatedContent: SuperLatexContent = {
      ...content,
      connections: content.connections?.filter((c) => c.id !== connectionId) ?? [],
    };

    this.contentChange.emit(updatedContent);
    this.editingConnection.set(null);
    this.selectedConnection.set(null);
  }

  deleteAnchor(anchorId: string): void {
    const content = this.content();
    const updatedNodes = content.latex_nodes.map((node) => {
      // Remove the htmlData wrapper for this anchor
      const pattern = new RegExp(`\\\\htmlData\\{anchor=${anchorId}\\}\\{([^}]*)\\}`, 'g');
      return {
        ...node,
        latex: node.latex.replace(pattern, '$1'),
      };
    });

    // Also remove any connections that use this anchor
    const updatedConnections = content.connections?.filter(
      (c) => c.from_anchor !== anchorId && c.to_anchor !== anchorId,
    );

    const updatedContent: SuperLatexContent = {
      ...content,
      latex_nodes: updatedNodes,
      connections: updatedConnections,
    };

    this.contentChange.emit(updatedContent);
  }

  saveConnectionEdit(): void {
    const editing = this.editingConnection();
    if (!editing) return;

    const content = this.content();
    const updatedContent: SuperLatexContent = {
      ...content,
      connections: content.connections?.map((c) => (c.id === editing.id ? editing : c)) ?? [],
    };

    this.contentChange.emit(updatedContent);
    this.editingConnection.set(null);
  }

  cancelConnectionEdit(): void {
    this.editingConnection.set(null);
  }

  updateConnectionStyle<K extends keyof SuperLatexConnectionStyle>(key: K, value: SuperLatexConnectionStyle[K]): void {
    const editing = this.editingConnection();
    if (!editing) return;

    this.editingConnection.set({
      ...editing,
      style: {
        ...editing.style,
        [key]: value,
      },
    });
  }

  getModeLabel(): string {
    switch (this.mode()) {
      case 'create-anchor':
        return 'Hover + scroll to resize selection, or drag to lasso multiple elements';
      case 'select-anchor':
        return 'Click on existing anchors to select/deselect';
      case 'connect-from':
        return 'Click on the source anchor';
      case 'connect-to':
        return 'Click on the target anchor';
      default:
        return '';
    }
  }

  copyAnchorsToClipboard(): void {
    const anchors = this.selectedAnchors();
    const json = JSON.stringify(anchors, null, 2);
    navigator.clipboard.writeText(json);
  }

  copyConnectionsToClipboard(): void {
    const connections = this.connections();
    const json = JSON.stringify(connections, null, 2);
    navigator.clipboard.writeText(json);
  }

  getRelativeLeft(rect: DOMRect): number {
    const container = this.latexContainer()?.nativeElement;
    if (!container) return 0;
    return rect.left - container.getBoundingClientRect().left;
  }

  getRelativeTop(rect: DOMRect): number {
    const container = this.latexContainer()?.nativeElement;
    if (!container) return 0;
    return rect.top - container.getBoundingClientRect().top;
  }

  getAnchorRect(anchorId: string): DOMRect | null {
    const container = this.latexContainer()?.nativeElement;
    if (!container) return null;

    const anchorEl = container.querySelector(`[data-anchor="${anchorId}"]`);
    if (!anchorEl) return null;

    return anchorEl.getBoundingClientRect();
  }

  // Computed for template - filters hoveredElement to only show anchors for certain modes
  readonly hoveredAnchor = computed(() => {
    const hovered = this.hoveredElement();
    if (!hovered?.isExistingAnchor) return null;
    return {
      id: hovered.anchorId,
      rect: hovered.rect,
    };
  });
}
